import { useDocumentBundleAuthStore } from '@repo/store/useDocumentBundleAuthStore'
import { useToastStore } from '@repo/store/useToastStore'

const BUNDLEDOCS_AUTH_REQUIRED_MESSAGE = 'You must authenticate with Bundledocs before performing this action.'
const BUNDLEDOCS_AUTH_DEPENDENCY_FAILED_STATUS = 424

export const isBundledocsAuthRequiredError = (err: any) => {
  const status = err?.status || err?.response?.status
  if (status === BUNDLEDOCS_AUTH_DEPENDENCY_FAILED_STATUS) return true

  const message =
    err?.response?.data?.detail || err?.response?.data?.title || err?.response?.data?.message || err?.message
  return typeof message === 'string' && message.includes(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
}

const MAX_ACTION_ATTEMPTS = 5
const CREDENTIAL_SETTLE_DELAY_MS = 1500

const AUTH_POLL_INTERVAL_MS = 2000
const MAX_AUTH_POLL_ATTEMPTS = 15 // ~30s, enough time for a user to enter Bundledocs credentials

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const authenticateWithBundledocs = async (bundleData: any) => {
  const { getLoginUrl, IsAuthenticatedUser } = useDocumentBundleAuthStore.getState()

  const loginUrl = await getLoginUrl({ clientNo: bundleData?.clientNo, matterNo: bundleData?.matterNo })
  if (!loginUrl) throw new Error('Unable to start Bundledocs authentication')

  window.open(loginUrl, '_blank', 'noopener,noreferrer')

  for (let attempt = 1; attempt <= MAX_AUTH_POLL_ATTEMPTS; attempt++) {
    await delay(AUTH_POLL_INTERVAL_MS)

    if (await IsAuthenticatedUser()) return
  }

  useToastStore.getState().showToast({ type: 'error', title: 'Error', text: BUNDLEDOCS_AUTH_REQUIRED_MESSAGE })
  throw new Error(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
}

export const ensureBundledocsActionCompleted = async (bundleData: any, performAction: any) => {
  const { IsAuthenticatedUser } = useDocumentBundleAuthStore.getState()

  const authenticated = await IsAuthenticatedUser()
  if (!authenticated) {
    await authenticateWithBundledocs(bundleData)
  }

  let hasAuthenticatedThisAttempt = false

  for (let attempt = 1; attempt <= MAX_ACTION_ATTEMPTS; attempt++) {
    const isLastAttempt = attempt === MAX_ACTION_ATTEMPTS

    if (hasAuthenticatedThisAttempt) {
      const stillAuthenticated = await IsAuthenticatedUser()

      if (!stillAuthenticated) {
        if (isLastAttempt) throw new Error(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
        await delay(CREDENTIAL_SETTLE_DELAY_MS)
        continue
      }
    }

    try {
      return await performAction(!isLastAttempt)
    } catch (err: any) {
      if (!isBundledocsAuthRequiredError(err) || isLastAttempt) throw err

      if (!hasAuthenticatedThisAttempt) {
        await authenticateWithBundledocs(bundleData)
        hasAuthenticatedThisAttempt = true
      }
    }
  }
}
